import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { RouteComponentProps, useHistory, useLocation, useParams, useRouteMatch } from "react-router"
import { mdiPlus, mdiDelete, mdiPencil, mdiTimerSand, mdiNotePlusOutline } from "@mdi/js"
import { isEmpty, cloneDeep, isEqual } from "lodash"

import { ColorPalette } from "../../config/colors"
import { PeopleFlowCombinedReducer } from "../../store"
import { ProfileService, SessionService, TableService } from "../../services"
import GroupMemberExpiryDateModal from "../../components/Groups/GroupMemberExpiryDateModal"
import { Toolbar, ActionButtonType } from "../../components/GeneralUI/Toolbar"
import { createActionRecord, getPpeInPossession, removeUnderScores } from "../../utils"
import { GroupMemberJobTypeModal } from "../../components/Groups/GroupMemberJobTypeModal"
import { generateMemberStatusIndicator } from "../../utils/groups"
import SideMenu from "../../components/Navigation/SideMenu"
import GroupMembersSideMenu from "../../components/SideMenus/GroupMembersSideMenu"
import { PpeService } from "../../services/ppeService"
import { GroupMemberPpeProps } from "../../components/Groups/GroupMemberPpe"
import AlertModalOneButton from "../../components/Modals/AlertModalOneButton"
import LoadingModal from "../../components/Modals/LoadingModal"
import NavBar from "../../components/Navigation/NavBar"
import SectionHeader from "../../components/Headings/SectionHeaderPrimary"
import { GroupService } from "../../services"
import { dateObjToString } from "../../utils/date"
import { JobRequirementsNotSatisfied } from "./JobRequirementsNotSatisfied"
import { AssignedTo } from "./AssignedTo"
import { GroupMemberDetails } from "./GroupMemberDetails"
import { RequestedPpeConfirmation } from "../../components/Ppe/RequestedPpeConfirmation"
import AlertModalTwoButton from "../../components/Modals/AlertModalTwoButton"
import { AssociationSettingsRepository, ProfileRepository } from "../../repositories"
import { Profile, ProfilePk } from "../../models"
import { FieldConfigKey, Group, GroupMemberProfile, PpeForProfile, PpeSelections, SubGroups } from "../../types"
import { PpeInstanceRecordSchema } from "../../models/ppe"
import { DocValidityStatusEnum, GeneralErrorCodesEnum, PpeActions } from "../../enums"
import { GroupMembers as GroupMembersType } from "../../types"
import { DocValidityService } from "../../services/docValidityService"

type RouteParams = { groupId: string }
type GroupMembersProps = RouteComponentProps

const ppeService = new PpeService()
const tableService = new TableService()
const profileService = new ProfileService()
const docValidityService = new DocValidityService()

const GroupMembers = (props: GroupMembersProps) => {
  const allGroupsRef = useRef<Group[]>([])
  const allPpeInstancesRef = useRef<PpeInstanceRecordSchema[]>([])

  const { groupId } = useParams() as RouteParams // group name passed as route param
  const history = useHistory()
  const match = useRouteMatch()
  const location = useLocation()

  const userIdPassport = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.idPassport)
  const userPassword = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.password)
  const selectedAssociation = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.selectedAssociation,
  )
  const selectedRole = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.selectedRole)
  const selectedCohort = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.selectedCohort)
  const profileRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.profileRepo,
  ) as ProfileRepository
  const navMenuAccess = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.navMenuAccess)
  const associationRepo = useSelector(
    (state: PeopleFlowCombinedReducer) => state.sessionManager.associationRepo,
  ) as AssociationSettingsRepository
  const userRepo = useSelector((state: PeopleFlowCombinedReducer) => state.sessionManager.userRepo)

  const [groupMemberProfiles, setGroupMemberProfiles] = useState<GroupMemberProfile[]>([])
  const [searchString, setSearchString] = useState("")
  const [loadingModalOpen, setLoadingModalOpen] = useState(true)
  const [warningModalOpen, setWarningModalOpen] = useState(false)
  const [warningModalConfig, setWarningModalConfig] = useState({ header: "", subHeader: null } as {
    header: string
    subHeader: React.ReactNode
  })
  const [expiryDateModalOpen, setExpiryDateModalOpen] = useState(false)
  const [jobTypeModalOpen, setJobTypeModalOpen] = useState(false)
  const [confirmStockRequestModalOpen, setConfirmStockRequestModalOpen] = useState(false)
  const [confirmPpeSelectionModalOpen, setConfirmPpeSelectionModalOpen] = useState(false)
  const [loadingModalMessage, setLoadingModalMessage] = useState("Loading data...")
  const [loadingModalAuxMessage, setLoadingModalAuxMessage] = useState("")
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([])
  const [selectedSubGroup, setSelectedSubGroup] = useState("All")
  const [groupMemberPpeSelection, setGroupMemberPpeSelection] = useState<GroupMemberPpeProps | {}>({})
  const [ppeSelectionModeEnabled, setPpeSelectionModeEnabled] = useState(false)
  const [requestedPpe, setRequestedPpe] = useState<PpeSelections>({})
  const [selectedPpe, setSelectedPpe] = useState<PpeForProfile>({})
  const [selectedGroup, setGroup] = useState<Group>()
  const [detailsRefreshTimestamp, setDetailsRefreshTimestamp] = useState(Date.now())

  useEffect(() => {
    const initialise = async () => {
      await initialisePpeInstances()
      const groupData = await initGroupData()
      const groupMemberIds = Object.keys(groupData.members)
      await profileService.downloadProfiles(
        selectedAssociation,
        profileRepo,
        { username: userIdPassport, password: userPassword },
        groupMemberIds,
      )
      await initGroupMemberValidCompentencies(groupData)
      setGroup(groupData)
    }

    const initGroupData = async (): Promise<Group> => {
      const token = await SessionService.prepareAuthTokens(userIdPassport, userPassword)
      const groupData = await GroupService.fetchGroup(selectedAssociation, selectedCohort, groupId, token)
      return groupData
    }

    const initGroupMemberValidCompentencies = async (groupData: Group) => {
      if (groupData && !isEmpty(groupData.members)) {
        let updatedGroup = cloneDeep(groupData)
        const authCredentials = { username: userIdPassport, password: userPassword }
        const memberPks = Object.keys(updatedGroup.members)
        await profileService.downloadProfiles(selectedAssociation, profileRepo, authCredentials, memberPks)
        await Promise.all(
          memberPks.map(async (memberPk: string) => {
            const docValidityRecordEntities = docValidityService.buildPreRequisiteReport(
              selectedAssociation,
              profileRepo,
              associationRepo,
              [memberPk],
            )
            const validDocs = docValidityService.getDocsInCategory(
              docValidityRecordEntities,
              DocValidityStatusEnum.VALID,
            )
            updatedGroup.members[memberPk].validTimeBasedDocs = validDocs.map((validDoc) =>
              validDoc.getPreRequisiteName(),
            )
            return updatedGroup.name
          }),
        )
        await saveGroup(updatedGroup)
      }
    }

    initialise()
  }, [groupId, selectedAssociation])

  useEffect(() => {
    if (selectedGroup) {
      loadProfileData()
    }
  }, [selectedGroup, GroupService.getNumberOfRequestedPpe(requestedPpe) /*, allProfiles*/])

  useEffect(() => {
    if (ppeSelectionModeEnabled && isEmpty(groupMemberPpeSelection)) {
      setPpeSelectionModeEnabled(false)
    }
  }, [ppeSelectionModeEnabled, groupMemberPpeSelection])

  useEffect(() => {
    if (loadingModalOpen === false && loadingModalMessage !== "") {
      setLoadingModalMessage("")
    }
  }, [loadingModalOpen])

  const subGroups = selectedGroup?.subGroups as SubGroups

  const toggleExpiryDateModal = () => {
    setExpiryDateModalOpen((expiryDateModalOpen) => !expiryDateModalOpen)
  }

  const toggleJobTypeModal = () => {
    setJobTypeModalOpen((jobTypeModalOpen) => !jobTypeModalOpen)
  }

  const initialisePpeInstances = async () => {
    const ppeInstances = await ppeService.getInstances(selectedAssociation, {
      username: userIdPassport,
      password: userPassword,
    })
    const allPpeInstances = Object.values(ppeInstances)
    allPpeInstancesRef.current = allPpeInstances
  }

  const refreshGroup = async () => {
    const token = await SessionService.prepareAuthTokens(userIdPassport, userPassword)
    const groupData = await GroupService?.fetchGroup(selectedAssociation, selectedCohort, groupId, token)
    setGroup(groupData)
    return groupData
  }

  const toggleConfirmStockRequestModal = () => {
    setConfirmStockRequestModalOpen((confirmStockRequestModalOpen) => !confirmStockRequestModalOpen)
  }

  const saveGroup = async (updatedGroup: Group) => {
    if (GroupService) {
      const token = await SessionService.prepareAuthTokens(userIdPassport, userPassword)
      await GroupService.postGroup(selectedAssociation, updatedGroup, token)
    }
    return
  }

  const saveNewJobTypesToSelectedMembers = async (jobType: string) => {
    if (selectedGroup) {
      let updatedGroup = cloneDeep(selectedGroup)
      selectedMemberIds.forEach((idPassport) => {
        const member = updatedGroup.members[idPassport]
        if (member) {
          member.jobType = jobType
        }
      })
      await saveGroup(updatedGroup)
      setGroup(updatedGroup)
    }
    setSelectedMemberIds([])
    toggleJobTypeModal()
    setDetailsRefreshTimestamp(Date.now())
  }

  const getHoverDetails = (rowData: GroupMemberProfile, memberJobType: string) => {
    const docsRequired = GroupService.getDocRequirementsNotSatisfied(
      rowData.id,
      memberJobType,
      associationRepo,
      selectedAssociation,
      selectedGroup,
    )
    const groupsBelongingTo = getNonExpiredGroupsBelongingTo(rowData.id)
    const belongsToMultipleGroups = groupsBelongingTo.length > 0

    let hoverDetails = {}
    if (docsRequired.length > 0) {
      hoverDetails = {
        ...hoverDetails,
        onHoverMessage: (
          <JobRequirementsNotSatisfied
            docsRequired={docsRequired}
            belongsToMultipleGroups={belongsToMultipleGroups}
            groupsBelongingTo={groupsBelongingTo}
          />
        ),
        hoverMessageType: "error",
      }
    } else if (belongsToMultipleGroups) {
      hoverDetails = {
        ...hoverDetails,
        onHoverMessage: <AssignedTo groupsBelongingTo={groupsBelongingTo} />,
        hoverMessageType: "info",
      }
    }

    return hoverDetails
  }

  const getProfileDataWithHoverMessagesAndStatusIndicators = (rowData: GroupMemberProfile) => {
    let data: GroupMemberProfile & {
      onHoverMessage?: string | React.ReactElement
      hoverMessageType?: "info" | "error"
      rightComponent?: React.ReactElement
    } = {
      ...rowData,
    }
    const memberJobType = GroupService.getMemberJobType(rowData.id, selectedGroup) // id equals member pk
    if (memberJobType) {
      const hoverDetails = getHoverDetails(rowData, memberJobType)
      data = {
        ...data,
        ...hoverDetails,
        rightComponent: generateMemberStatusIndicator(data.startDate, data.terminationDate),
      }
    }
    return data
  }

  const loadProfileData = async () => {
    setLoadingModalOpen(true)
    if (selectedGroup && selectedGroup.members) {
      if (selectedAssociation !== profileRepo.getAssociation()) {
        throw new Error(GeneralErrorCodesEnum.AssociationMismatch)
      }
      const groupMemberProfiles = new Map<string, Profile>()
      Object.keys(selectedGroup.members).forEach((pk) => {
        const profile = profileRepo.getProfileEntity(pk)
        if (profile) {
          groupMemberProfiles.set(pk, profile)
        }
      })
      updateProfileToDisplay(groupMemberProfiles)
    }
    setLoadingModalOpen(false)
    setDetailsRefreshTimestamp(Date.now())
  }

  const updateProfileToDisplay = (groupMemberProfiles: any) => {
    const profileData: GroupMemberProfile[] = prepareProfileDataForDisplay(groupMemberProfiles)
    const profilesToDisplay = profileData.map(getProfileDataWithHoverMessagesAndStatusIndicators)
    setGroupMemberProfiles(profilesToDisplay)
  }

  const getPpeSelectedCount = (profilePk: ProfilePk) => {
    if (requestedPpe[profilePk]) {
      const requestedPpeValues = Object.values(requestedPpe[profilePk])
      const nonEmptyPpeValues = requestedPpeValues.filter((value) => value && value !== "")
      return nonEmptyPpeValues.length
    }
    return 0
  }

  const renderRow = (profile: any) => {
    const pk = profile[0]
    const value = profile[1]
    let { name, surname, idPassport } = value.getPersonalInfo()

    if (Array.isArray(name) && name.length) {
      name = name[0]
    }
    if (Array.isArray(surname) && surname.length) {
      surname = surname[0]
    }
    if (Array.isArray(idPassport) && idPassport.length) {
      idPassport = idPassport[0]
    }

    const groupMember = selectedGroup?.members[pk]
    const groupMemberJobType = groupMember ? groupMember.jobType : undefined
    const groupMemberProposedStartDate = groupMember ? dateObjToString(groupMember.proposedStartDate) : undefined
    const groupMemberProposedTerminationDate = groupMember
      ? dateObjToString(groupMember.proposedTerminationDate)
      : undefined

    const ppeSelectedCount = getPpeSelectedCount(pk)
    const ppeIssuedAndNotReturned = getPpeInPossession(profileRepo, pk)
    const ppeIssuedCount = Object.keys(ppeIssuedAndNotReturned).length

    let returnData = {
      id: pk,
      name: name,
      surname: surname,
      idPassport,
      jobType: removeUnderScores(groupMemberJobType) || "-",
      startDate: groupMemberProposedStartDate || "-",
      terminationDate: groupMemberProposedTerminationDate || "-",
      ppeStatus: (
        <div style={{ textAlign: "left" }}>
          {`Issued (${ppeIssuedCount})`}
          <br />
          {`Selected (${ppeSelectedCount})`}
        </div>
      ),
    }
    return returnData
  }

  const prepareProfileDataForDisplay = (allProfiles: Map<string, Profile> | undefined): GroupMemberProfile[] => {
    if (allProfiles === undefined) {
      return []
    }
    let allProfilesArray = [...allProfiles]
    const allProfilesToRender = allProfilesArray.filter((profile) => {
      const value = profile[1]
      if (value) {
        let generalData: any = value.getGeneralData()
        if (generalData === undefined) {
          generalData = { employmentStatus: "NONE" }
        }
        if (!value.isPartOfCohort(selectedCohort)) {
          return false
        }

        const { employmentStatus, name, surname, idPassport } = generalData
        if (name && surname && idPassport && ["CANDIDATE", "EMPLOYEE"].indexOf(employmentStatus) > -1) {
          return true
        }
      }
      return false
    })

    return allProfilesToRender.map(renderRow)
  }

  const searchHandler = (event: React.ChangeEvent<{ value: string }>) => {
    setSearchString(event.target.value)
  }

  const removeSelectedGroupMembers = async () => {
    setLoadingModalMessage("Refreshing member data...")
    setLoadingModalOpen(true)
    if (selectedGroup) {
      const remainingMembers = Object.keys(selectedGroup.members).reduce(
        (acc: GroupMembersType, idPassport: string) => {
          if (!selectedMemberIds.includes(idPassport)) {
            acc[idPassport] = selectedGroup.members[idPassport]
          }
          return acc
        },
        {},
      )
      let updatedGroup = { ...selectedGroup, members: cloneDeep(remainingMembers) }
      await saveGroup(updatedGroup)
      setGroup(updatedGroup)
    }
    setLoadingModalOpen(false)
    setSelectedMemberIds([])
  }

  const anySelectedMemberIds = selectedMemberIds.length > 0

  const getNonExpiredGroupsBelongingTo = (idPassport: string) => {
    if (selectedGroup && allGroupsRef && allGroupsRef.current) {
      const allGroups = allGroupsRef.current
      const allNonExpiredGroups = allGroups.filter((selectedGroup: Group) => selectedGroup.endDate >= new Date())
      const otherGroups = allNonExpiredGroups.filter((otherGroup: Group) => selectedGroup.id !== otherGroup?.id)
      let groups = []
      for (const otherGroup of otherGroups) {
        if (otherGroup.members) {
          for (const memberIdPassport of Object.keys(otherGroup.members)) {
            if (memberIdPassport === idPassport) {
              groups.push(otherGroup.name)
            }
          }
        }
      }
      return groups
    }
    return []
  }

  const checkIfSubGroupSelected = () => selectedSubGroup !== "All"

  const getSelectedMembersMinStartDate = () => {
    const minStartDate: Date = GroupService.getGroupMembersMinStartDate(selectedMemberIds, selectedGroup)
    return minStartDate
  }

  const getSelectedMembersMaxExpiryDate = (expiryDate?: Date) => {
    if (selectedGroup) {
      const groupMembers = selectedGroup.members || []
      const numberOfSelectedGroupMembers = selectedMemberIds.length
      let groupStartDate = selectedGroup.startDate || new Date()
      let groupEndDate = selectedGroup.endDate || new Date()
      let minGroupMemberEndDate = new Date(groupEndDate)

      if (numberOfSelectedGroupMembers === 1) {
        const selectedIdPassport = selectedMemberIds[0]
        const selectedGroupMember = groupMembers[selectedIdPassport]
        if (selectedGroupMember) {
          const groupMemberEndDate = expiryDate || selectedGroupMember.proposedTerminationDate
          if (
            groupMemberEndDate &&
            groupMemberEndDate > groupStartDate &&
            groupMemberEndDate < minGroupMemberEndDate &&
            groupMemberEndDate < groupEndDate
          ) {
            minGroupMemberEndDate = groupMemberEndDate
          }
          return minGroupMemberEndDate
        }
      }
      return groupEndDate
    }
    return new Date()
  }

  const handleDateSelection = async (startDate?: Date, expiryDate?: Date) => {
    setLoadingModalMessage("Saving changes..")
    setLoadingModalOpen(true)
    const newStartDate = dateObjToString(startDate)
    const newTerminationDate = dateObjToString(expiryDate)
    const updatedGroup = cloneDeep(selectedGroup)
    await Promise.all(
      selectedMemberIds.map((idPassport) => {
        if (selectedGroup) {
          const groupMember = updatedGroup?.members[idPassport]
          if (groupMember) {
            const oldStartDate = dateObjToString(groupMember.proposedStartDate)
            const oldTerminationDate = dateObjToString(groupMember.proposedTerminationDate)
            if (newStartDate && oldStartDate !== newStartDate) {
              if (groupMember) {
                groupMember.proposedStartDate = new Date(newStartDate)
              }
            }
            if (newTerminationDate && oldTerminationDate !== newTerminationDate) {
              if (groupMember) {
                groupMember.proposedTerminationDate = new Date(newTerminationDate)
              }
            }
            //@ts-ignore
            return saveGroup(updatedGroup)
          }
        }
        return
      }),
    )
    setLoadingModalOpen(false)
    await handleDateSelectionSaved()
  }

  const handleDateSelectionSaved = async () => {
    setSelectedMemberIds([])
    await refreshGroup()
    toggleExpiryDateModal()
    setDetailsRefreshTimestamp(Date.now())
  }

  const getSelectedMemberJobType = () => {
    if (selectedGroup) {
      const groupMembers = selectedGroup.members || []
      const numberOfSelectedGroupMembers = selectedMemberIds.length
      if (numberOfSelectedGroupMembers === 1) {
        const selectedIdPassport = selectedMemberIds[0]
        const selectedGroupMember = groupMembers[selectedIdPassport]
        if (selectedGroupMember) {
          return selectedGroupMember.jobType
        }
      }
    }
    return ""
  }

  const getActionButtons = () => {
    let actionButtons: ActionButtonType[] = []
    const subGroupSelected = checkIfSubGroupSelected()
    if (!groupCompleted) {
      if (!isEmpty(groupMemberPpeSelection)) {
        return actionButtons
      } else if (subGroupSelected) {
        actionButtons = [
          {
            iconPath: mdiNotePlusOutline,
            iconColor: ColorPalette.PRIMARY_BLUE,
            onClick: toggleConfirmStockRequestModal,
            label: "CREATE REQUEST",
            title: "Create request for PPE",
          },
        ]
      } else {
        const iconColour = anySelectedMemberIds ? ColorPalette.PRIMARY_BLUE : ColorPalette.MEDIUM_GREY
        const iconStyle = anySelectedMemberIds ? {} : { cursor: "not-allowed" }

        actionButtons = [
          {
            iconPath: mdiPlus,
            iconColor: ColorPalette.PRIMARY_BLUE,
            onClick: () => history.push(`/people/groups/${groupId}/add`),
            label: "ADD",
            title: "Add member(s)",
          },
          {
            iconPath: mdiDelete,
            iconColor: iconColour,
            onClick: removeSelectedGroupMembers,
            label: "REMOVE",
            disabled: !anySelectedMemberIds,
            style: iconStyle,
          },
          {
            iconPath: mdiPencil,
            iconColor: iconColour,
            onClick: toggleJobTypeModal,
            label: "CHANGE JOB TYPE",
            title: "Set job type on selected records",
            disabled: !anySelectedMemberIds,
            style: iconStyle,
          },
          {
            iconPath: mdiTimerSand,
            iconColor: iconColour,
            onClick: toggleExpiryDateModal,
            label: "SET DATE RANGE",
            title: "Set date range on selected records",
            disabled: !anySelectedMemberIds,
            style: iconStyle,
          },
        ]
      }
    }
    return actionButtons
  }

  const handlePpeSelections = (selectionsPending: PpeForProfile) => {
    setSelectedPpe(selectionsPending)
  }

  const resetSelections = () => {
    setSelectedPpe({})
    setSelectedMemberIds([])
    setGroupMemberPpeSelection({})
    setPpeSelectionModeEnabled(false)
  }

  const handlePpeSelectionDiscard = () => {
    resetSelections()
    setConfirmPpeSelectionModalOpen(false)
  }

  const handlePpeSelectionSave = () => {
    setConfirmPpeSelectionModalOpen(false)
    let profilePk = (groupMemberPpeSelection as GroupMemberPpeProps).profilePk
    let updatedRequestedPpe = cloneDeep(requestedPpe)
    updatedRequestedPpe[profilePk] = selectedPpe
    resetSelections()
    setRequestedPpe(updatedRequestedPpe)
  }

  const createPpeRequest = async () => {
    const user = userRepo?.getCurrentUserEntity()
    if (!user) {
      return
    }
    const record = createActionRecord({
      ppeAction: PpeActions.SELECT_SIZE,
      association: selectedAssociation,
      division: selectedGroup?.division,
      dateActioned: Date.now(),
      requestedPpeItems: GroupService.extractRequestedPpeItems(requestedPpe),
      sourceName: user.getName(),
      sourceSurname: user.getSurname(),
      sourcePk: userIdPassport,
      seedGroupId: GroupService.getSubGroupIdByDisplayName(subGroups, "ppe", selectedSubGroup),
    })
    return ppeService.uploadActionRecord(selectedAssociation, [record], {
      username: userIdPassport,
      password: userPassword,
    })
  }

  const generateAndDisplayUnavailableItemsWarning = (
    unavailablePpeItems: Record<FieldConfigKey, Record<string, number>>,
  ) => {
    const messages = ppeService.generateUnavailableItemMessageList(
      unavailablePpeItems,
      associationRepo,
      selectedAssociation,
    )
    const header = "Items Unavailable"
    const subHeader = messages.map((message, i) => <p key={`displayWarning_${i}`}>{message}</p>)
    setWarningModalConfig({ header, subHeader })
    setWarningModalOpen(true)
  }

  const handleNewPpeRequest = async () => {
    toggleConfirmStockRequestModal()
    setLoadingModalMessage("Creating new PPE request...")
    setLoadingModalOpen(true)
    try {
      const response = await createPpeRequest()
      if (response.unavailablePpeItems) {
        const unavailablePpeItems: Record<string, any> = response.unavailablePpeItems
        generateAndDisplayUnavailableItemsWarning(unavailablePpeItems)
      } else {
        history.push("/requests/pperequests")
      }
    } catch (error) {
      console.error("handleNewPpeRequest", error)
    }
    setLoadingModalOpen(false)
  }

  const getPageTitle = (subGroupTabSelected: boolean, ppeSelectionModeEnabled: boolean) => {
    let title = selectedGroup ? selectedGroup.name : "Manage Group Members"
    if (subGroupTabSelected) {
      title = ppeSelectionModeEnabled ? "Select PPE" : "Request PPE"
    }
    return title
  }

  const reloadPage = () => {
    setSearchString("")
    loadProfileData()
  }

  const handleBackButtonClick = () => {
    function hasPpeSelection(selectedPpe: PpeForProfile = {}): boolean {
      return Object.values(selectedPpe).some((value) => value.length > 0)
    }

    function requirePpeSelectionConfirmation(
      profilePk: string,
      requestedPpe: PpeSelections,
      selectedPpe: PpeForProfile,
    ): boolean {
      const profileRequestedPpe = requestedPpe[profilePk]
      const newSelection = profileRequestedPpe === undefined
      if (newSelection) {
        return hasPpeSelection(selectedPpe)
      }
      return !isEqual(profileRequestedPpe, selectedPpe)
    }

    let profilePk = (groupMemberPpeSelection as GroupMemberPpeProps).profilePk
    if (requirePpeSelectionConfirmation(profilePk, requestedPpe, selectedPpe)) {
      setConfirmPpeSelectionModalOpen(true)
      return
    }
    if (ppeSelectionModeEnabled) {
      setGroupMemberPpeSelection({})
      setPpeSelectionModeEnabled(false)
    } else {
      history.replace("/people/groups")
    }
  }

  const handleDetailsRowSelect = (selectedRows: any) => setSelectedMemberIds(Object.keys(selectedRows))

  const handleDetailsRowClick = (selectedRowData: Record<string, any>) => {
    const { id, jobType, startDate, location, name, surname, idPassport } = selectedRowData
    setGroupMemberPpeSelection({
      profilePk: id,
      jobType,
      scheduledStartDate: startDate,
      location,
      name,
      surname,
      idPassport,
    })
    setPpeSelectionModeEnabled(true)
  }

  const handleSideMenuItemSelection = (option: string) => {
    setPpeSelectionModeEnabled(false)
    setSelectedSubGroup(option)
  }

  const groupCompleted = selectedGroup && new Date() > selectedGroup.endDate
  let sideMenu = null
  if (!groupCompleted && navMenuAccess.hasPpeRequestAccess) {
    sideMenu = (
      <SideMenu
        visible={true}
        menuComponents={
          <GroupMembersSideMenu
            itemNames={GroupService.getSubGroupNames(subGroups, "ppe")}
            selectedItemName={selectedSubGroup}
            onItemSelection={handleSideMenuItemSelection}
          />
        }
      />
    )
  }

  let jobTypeModal = null
  if (selectedGroup && jobTypeModalOpen) {
    jobTypeModal = (
      <GroupMemberJobTypeModal
        group={selectedGroup}
        initialJobType={getSelectedMemberJobType()}
        onClick={saveNewJobTypesToSelectedMembers}
        onDismiss={toggleJobTypeModal}
      />
    )
  }

  const getPageDetails = (
    isSubGroupSelected: boolean,
    ppeSelectionModeEnabled: boolean,
    selectedGroupCategory = "ppe",
  ) => {
    const profilePk = (groupMemberPpeSelection as GroupMemberPpeProps).profilePk
    const memberProfileData = tableService.applySearch(searchString, groupMemberProfiles)
    return (
      <GroupMemberDetails
        memberProfiles={memberProfileData}
        selectedGroup={selectedGroup}
        selectedSubGroup={selectedSubGroup}
        subGroupSelected={isSubGroupSelected}
        selectedGroupCategory={selectedGroupCategory}
        groupMemberPpeSelection={groupMemberPpeSelection}
        groupCompleted={!!groupCompleted}
        loadingModalOpen={loadingModalOpen}
        ppeSelectionModeEnabled={ppeSelectionModeEnabled}
        allPpeInstances={allPpeInstancesRef.current}
        ppeSelections={requestedPpe[profilePk]}
        setPpeSelections={(selectionsPending: Record<string, string>) => handlePpeSelections(selectionsPending)}
        getNonExpiredGroupsBelongingTo={getNonExpiredGroupsBelongingTo}
        onRowSelect={handleDetailsRowSelect}
        onRowClick={handleDetailsRowClick}
        key={`groupMemberDetails-${profilePk}_${detailsRefreshTimestamp}`}
      />
    )
  }

  const isSubGroupSelected = checkIfSubGroupSelected()
  const title = getPageTitle(isSubGroupSelected, ppeSelectionModeEnabled)
  const pageContent = getPageDetails(isSubGroupSelected, ppeSelectionModeEnabled)
  const initialStartDate = getSelectedMembersMinStartDate()
  const initialExpiryDate = getSelectedMembersMaxExpiryDate()
  const numberOfRequestedPpe = GroupService.getNumberOfRequestedPpe(requestedPpe)
  const numberOfPeopleWithRequestedPpe = Object.keys(requestedPpe).length
  const actionButtons = getActionButtons()

  let warningModal = null
  if (warningModalOpen) {
    warningModal = (
      <AlertModalOneButton
        open={true}
        header={warningModalConfig.header}
        subHeader={warningModalConfig.subHeader}
        buttonLabel="Ok"
        onClick={() => setWarningModalOpen(false)}
      />
    )
  }

  let confirmStockRequestModal = null
  if (confirmStockRequestModalOpen) {
    if (numberOfRequestedPpe === 0) {
      confirmStockRequestModal = (
        <AlertModalOneButton
          open={true}
          header="No PPE selected"
          body="Please select PPE items for one or more members to create request."
          buttonLabel="Ok"
          onClick={toggleConfirmStockRequestModal}
        />
      )
    } else {
      confirmStockRequestModal = (
        <AlertModalTwoButton
          open={true}
          dismiss={toggleConfirmStockRequestModal}
          onClick1={toggleConfirmStockRequestModal}
          onClick2={handleNewPpeRequest}
          header="Are you sure?"
          buttonLabel1="Back"
          buttonLabel2="Yes, Continue"
          buttonDisabled2={false} // no stock items selected?
          buttonStyle={{ marginTop: 30 }}
          body={
            <RequestedPpeConfirmation
              numberOfRequestedPpe={numberOfRequestedPpe}
              numberOfPeopleWithRequestedPpe={numberOfPeopleWithRequestedPpe}
            />
          }
        />
      )
    }
  }

  const { hasGroupAccess } = navMenuAccess
  return (
    <div style={{ ...styles.container }}>
      <NavBar match={match} location={location} history={history} reloadPageData={reloadPage} />
      <SectionHeader
        style={styles.sectionHeader}
        searchString={searchString}
        textHandler={(e) => searchHandler(e)}
        onClick={() => null}
        key={`sectionHeader_${selectedAssociation}`}>
        {title}
      </SectionHeader>

      <div style={styles.contentContainer}>
        {sideMenu}
        <div style={styles.rightSide}>
          <Toolbar
            actionButtons={actionButtons}
            navButtons={{ left: { onClick: handleBackButtonClick } }}
            key={selectedSubGroup}
          />
          <div style={styles.rightSideContent}>{pageContent}</div>
        </div>
      </div>
      <AlertModalOneButton
        open={!hasGroupAccess}
        header="Not Authorised"
        body="You don't have permission to view employee/candidate info."
        buttonLabel="Ok"
        opaqueBackground={true}
        onClick={() => history.goBack()}
      />
      <GroupMemberExpiryDateModal
        open={!!(selectedGroup && expiryDateModalOpen)}
        group={selectedGroup}
        initialStartDate={initialStartDate}
        initialExpiryDate={initialExpiryDate}
        toggle={toggleExpiryDateModal}
        onDateSelection={handleDateSelection}
        key={`groupMemberExpiryDateModal_${initialStartDate}-${initialExpiryDate}`}
      />
      <LoadingModal
        open={loadingModalOpen}
        loadingMessage={loadingModalMessage}
        auxilliaryMessage={loadingModalAuxMessage}
      />

      {jobTypeModal}
      {warningModal}
      {confirmStockRequestModal}
      <AlertModalOneButton
        // TODO: There should only be one AlertModalOneButton component per screen with the required headers, body, and functions being set accordingly by a handler.
        open={!navMenuAccess.hasGroupAccess}
        header={"Not Authorised"}
        body={"You don't have permission to view/edit groups."}
        buttonLabel={"Ok"}
        opaqueBackground={true}
        onClick={() => props.history.goBack()}
      />
      <AlertModalTwoButton
        open={confirmPpeSelectionModalOpen}
        dismiss={() => setConfirmPpeSelectionModalOpen(false)}
        onClick1={handlePpeSelectionDiscard}
        onClick2={handlePpeSelectionSave}
        header="Save selection(s)?"
        buttonLabel1="Discard"
        buttonLabel2="Save"
        buttonDisabled2={false}
        buttonStyle={{ marginTop: 30 }}
        body="PPE selection changes have been made"
      />
    </div>
  )
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    backgroundImage: `linear-gradient(to bottom, ${ColorPalette.SCREEN_TOP_GRADIENT}, ${ColorPalette.SCREEN_BOTTOM_GRADIENT})`,
    height: "100vh",
  },
  sectionHeader: {
    margin: "3.5% auto 1%",
  },
  contentContainer: {
    display: "flex",
    flex: 1,
    overflow: "auto",
  },
  rightSide: {
    display: "flex",
    flexDirection: "column" as "column",
    paddingInline: "min(2em, 2%)",
    width: "100%",
    overflow: "hidden",
  },
  rightSideContent: {
    boxShadow: "0px -1px 8px rgba(60,60,60, 0.1)",
    display: "flex",
    flex: 1,
    backgroundColor: ColorPalette.CARD_WHITE,
  },
}

export default GroupMembers
